import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const OvertimePay = () => {
  // State variables for Overtime Pay calculation
  const [overtimeHours, setOvertimeHours] = useState(0); // Number of overtime hours worked
  const [overtimeRate, setOvertimeRate] = useState(0); // Overtime pay rate (e.g., 1.5x or 2x regular pay)
  const [overtimePay, setOvertimePay] = useState(0); // Calculated overtime pay

  // Event handlers to update state values
  const handleOvertimeHoursChange = (e) => setOvertimeHours(parseFloat(e.target.value) || 0);
  const handleOvertimeRateChange = (e) => setOvertimeRate(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate overtime pay when any input changes
  useEffect(() => {
    if (overtimeHours >= 0 && overtimeRate > 0) {
      const pay = overtimeHours * overtimeRate;
      setOvertimePay(pay);
    } else {
      setOvertimePay(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [overtimeHours, overtimeRate]);

  return (
    <Layout title="Overtime Pay Calculator" path="overtime-pay">
      <section className="section-padding" id="overtime-pay-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Overtime Pay Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Overtime Pay Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Overtime Hours Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="overtimeHours" className="form-label">
                        Overtime Hours Worked
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Overtime Hours Worked:</strong> The number of hours worked beyond the normal working hours. These hours are usually compensated at a higher rate."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="overtimeHours"
                        placeholder="Enter overtime hours worked"
                        value={overtimeHours || ""}
                        onChange={handleOvertimeHoursChange}
                      />
                    </div>

                    {/* Overtime Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="overtimeRate" className="form-label">
                        Overtime Rate (Multiplier)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Overtime Rate:</strong> The multiplier that represents how much higher the overtime pay rate is compared to the regular hourly wage. For example, an overtime rate of 1.5 means 1.5 times the regular hourly rate."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="overtimeRate"
                        placeholder="Enter overtime rate (e.g., 1.5, 2)"
                        value={overtimeRate || ""}
                        onChange={handleOvertimeRateChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Overtime Pay:{" "}
                        <span className="fw-bold ms-3">
                          {overtimePay === 0 ? "0.00" : overtimePay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Overtime Pay Calculator
                </h3>

                {/* What is an Overtime Pay Calculator? */}
                <h4 className="mt-3 mb-3">What is an Overtime Pay Calculator?</h4>
                <p>
                  The **Overtime Pay Calculator** helps you calculate the total pay for overtime hours worked, which is typically calculated at a higher rate than regular pay (e.g., 1.5x or 2x).
                </p>

                {/* Why Use an Overtime Pay Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an Overtime Pay Calculator?</h4>
                <p>
                  This calculator ensures accurate employee compensation for overtime hours worked, making payroll management easier and ensuring compliance with labor laws.
                </p>

                {/* Benefits of Using an Overtime Pay Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using an Overtime Pay Calculator</h4>
                <ul>
                  <li><strong>Ensures Accurate Compensation:</strong> Helps in calculating correct overtime pay according to the overtime rate.</li>
                  <li><strong>Reduces Payroll Errors:</strong> Avoids mistakes in employee compensation for overtime work.</li>
                  <li><strong>Compliance with Labor Laws:</strong> Ensures that overtime pay is calculated in accordance with local or national labor regulations.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use an Overtime Pay Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>HR Departments:</strong> To ensure correct overtime compensation for employees.</li>
                  <li><strong>Payroll Teams:</strong> To simplify the calculation of employee overtime pay during payroll processing.</li>
                  <li><strong>Finance Teams:</strong> To help manage payroll expenses and ensure proper budget allocations for overtime payments.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is the standard overtime rate?</h4>
                <p>
                  The standard overtime rate is typically 1.5x (time-and-a-half) the regular hourly rate, though it can be 2x (double time) or higher, depending on local labor laws or company policies.
                </p>

                <h4 className="mt-4 mb-3">Do all employees qualify for overtime pay?</h4>
                <p>
                  Not all employees qualify for overtime pay. Typically, non-exempt employees (those who are not salaried or in specific managerial roles) are entitled to overtime pay, while exempt employees are not.
                </p>

                <h4 className="mt-4 mb-3">How is overtime pay calculated?</h4>
                <p>
                  Overtime pay is calculated by multiplying the number of overtime hours worked by the overtime rate (e.g., 1.5 or 2 times the regular hourly rate).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default OvertimePay;
